import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames';
import Container from './Container';
import Button from './Button';
import ImageModal from "./modals/ImageModal";
import SignatureModal from './modals/SignatureModal';
import LargeLoader from './LargeLoader';
import useApiRequest from '../utils/useApiRequest';
import useSmallFooter from '../utils/useSmallFooter';
import { sendEvent } from '../utils/analytics';
import { rgbToHex } from '../utils/rgbToHex';
import { initDrag, initMoveByKeys, initResize, initTextEditor } from '../utils/editor';
import getErrorName from '../utils/getErrorName';
import s, { ConvertResponseStruct } from '../utils/struct';
import useEditorStore from '../store/useEditorStore';
import { EDITOR_TOOL_VALUES } from '../utils/editorTools';
import ErrorModal from './ErrorModal';
import EditorPanelButton from "./panelComponents/buttons/EditorPanelButton";
import IconText from '../public/images/text.svg'
import IconDelete from '../public/images/delete-2.svg'
import EditorPanel from "./panelComponents/EditorPanel";
import useEditorPanelButtonStore from "../store/useEditorPanelButtonStore";
import useObjectEditStore from "../store/useObjectEditStore";
import SignSelect from "./panelComponents/select/SignSelect";
import FormSelect from "./panelComponents/select/FormSelect";
import ImageSelect from "./panelComponents/select/ImageSelect";
import HighLightSelect from "./panelComponents/select/HighLightSelect";

const getFileFormat = (width, height, zoom) => {
  // formats with DPI = 72 (default for zoom = 1 in pdf2htmlex)
  const formats = {
    a0: {
      name: 'A0',
      size: 3370 * 2384 * zoom ** 2,
    },
    a1: {
      name: 'A1',
      size: 2384 * 1685 * zoom ** 2,
    },
    a2: {
      name: 'A2',
      size: 1685 * 1192 * zoom ** 2,
    },
    a3: {
      name: 'A3',
      size: 1192 * 842 * zoom ** 2,
    },
    a4: {
      name: 'A4',
      size: 842 * 596 * zoom ** 2,
    },
  };

  const format = Object.keys(formats).find((key) => {
    const square = width * height;
    const ratio = formats[key].size / square;

    return ratio > 0.9 && ratio < 1.1;
  });

  return { format: formats[format]?.name || formats.a4.name };
};

const getFileOrientation = (width, height) => {
  return width > height ? { landscape: true } : { landscape: false };
};

const PdfEditor = ({
  disabled,
  onSave,
  zoom,
}) => {
  const instanceRef = useRef(null);
  const { t } = useTranslation();
  const router = useRouter();
  const store = useEditorStore();
  const editorPanelButtonStore = useEditorPanelButtonStore();
  const objectEditStore = useObjectEditStore();
  const [keyEventCount, setKeyEventCount] = useState(0);
  const toolApplied = useRef(false);

  const scope = useRef({
    scaleFactor: 0,
    offset: {
      y: 0,
      x: 0,
    },
  });

  const htmlFileRequest = useApiRequest({ url: `/api/storage/${store.uploadFileResponce.hash}.html`, method: 'get' });
  const {
    sendApiRequest,
    processing,
    error: editError,
  } = useApiRequest({ url: '/api/html/to/pdf' });

  // Deep-link из карточек/меню (?tool=sign|form) открывает соответствующую
  // панель редактора. Эффект срабатывает при маунте PdfEditor, а он монтируется
  // уже после загрузки файла (флаг processing лишь глушит срабатывание во время
  // сохранения). После применения стираем ТОЛЬКО ключ tool из query (shallow,
  // сохраняя utm_*/pathname), иначе на следующем файле панель открылась бы снова.
  useEffect(() => {
    if (processing || toolApplied.current) {
      return;
    }

    const tool = router.query.tool;

    if (EDITOR_TOOL_VALUES.includes(tool)) {
      editorPanelButtonStore.setTab(tool);
      editorPanelButtonStore.setIsOpenModal(tool);
      toolApplied.current = true;
      const query = { ...router.query };
      delete query.tool;
      router.replace({ pathname: router.pathname, query }, undefined, { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing, router.query.tool]);

  const handleSaveClick = useCallback(async () => {
    sendEvent({
      category: 'pdf-editor',
      action: 'save-click',
    });

    clearEdit();
    const styles = `
      <style type="text/css">
        .obj img {
          max-width: unset;
          width: 100%;
        }
      </style>
    `;

    const html = instanceRef.current.innerHTML + styles;
    const params = JSON.stringify(setPrintPageParams());

    sendApiRequest({
      data: {
        filename: store.uploadFileResponce.hash + '.pdf',
        html: html,
        params: params,
      },
      validateResponse(response) {
        s.assert(response.data, ConvertResponseStruct);
      },
      onSuccess(response, time) {
        sendEvent({
          category: 'html-to-pdf',
          action: 'success',
          label: `${time}`,
        });
        onSave(response.data.result[0]);
      },
      onError(error, time) {
        sendEvent({
          category: 'html-to-pdf',
          action: 'error',
          label: `${getErrorName(error)}:${time}`,
        });
      },
    });
  }, [store.uploadFileResponce, onSave, sendApiRequest]);

  const handleAddTextClick = useCallback((event) => {
    sendEvent({
      category: 'pdf-editor',
      action: 'change-tab',
      label: 'add',
    });
    editorPanelButtonStore.setTab('edit');
    clearEdit();

    if (document.querySelector('.object-dragable')) {
      return;
    }
    scope.current.offset.y = 10;
    scope.current.offset.x = 60;
    const objectElement = document.createElement('div');
    objectElement.className = 'obj obj-text';
    objectElement.style.fontSize = '20px';
    objectElement.style.lineHeight = '1';
    objectElement.innerHTML = `${t('convert-and-edit:editor.text-example')}`;
    selectObject(event, objectElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const selectSignature = useCallback((event) => {
    if (document.querySelector('.object-dragable')) {
      return;
    }
    editorPanelButtonStore.setTab('sign')
    scope.current.offset.y = 45;
    scope.current.offset.x = 100;
    const signature = event.target.src;
    const objectElement = document.createElement('div');
    objectElement.style.height = '97px';
    objectElement.style.width = '320px';
    objectElement.className = 'obj obj-resizable';
    objectElement.innerHTML = `<img class="signature-image" alt="" src="${signature}">`;
    selectObject(event, objectElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectImage = useCallback((event) => {
    if (document.querySelector('.object-dragable')) {
      return;
    }
    editorPanelButtonStore.setTab('image');
    scope.current.offset.y = 45;
    scope.current.offset.x = 100;
    const photo = event.target.src;
    const objectElement = document.createElement('div');
    objectElement.className = 'obj obj-resizable';
    objectElement.innerHTML = `<img class="photo-image" alt="" src="${photo}">`;
    selectObject(event, objectElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectForms = useCallback((event, form) => {
    if (document.querySelector('.object-dragable')) {
      return;
    }
    editorPanelButtonStore.setTab('form');
    scope.current.offset.y = 15;
    scope.current.offset.x = 10;
    const objectElement = document.createElement('div');
    objectElement.className = 'obj obj-resizable obj-form';
    objectElement.style.height = '30px';

    if(form.includes('input')) {
      objectElement.style.width = '215px';
      objectElement.innerHTML = `<input class='form-input' type='text'/>`
    }
    else {
      objectElement.style.width = '30px';
      objectElement.innerHTML = `<img class="form-image" style="position: absolute" alt="" src="${form}">`
    }
    selectObject(event, objectElement);
    editorPanelButtonStore.setIsOpenModal('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signatureButtons = editorPanelButtonStore.signatureList.map((signature, index) =>
    <div className="pdf-editor__container" key={index}>
      <Button
        className="pdf-editor__button"
        size="sm"
        onClick={selectSignature}
      >
        <img alt="" src={signature} />
      </Button>
      <button onClick={() => editorPanelButtonStore.setSignatureList(
        editorPanelButtonStore.signatureList.filter((_, id) => id !== index))}>
        <IconDelete className="pdf-editor__icon"/>
      </button>
    </div>,
  );

  const imageButtons = editorPanelButtonStore.imageList.map((image, index) =>
    <div className="pdf-editor__container" key={index}>
      <Button
        className="pdf-editor__button"
        size="sm"
        onClick={selectImage}
      >
        <img alt="" src={image} />
      </Button>
      <button onClick={() => editorPanelButtonStore.setImageList(editorPanelButtonStore.imageList.filter((_, id) => id !== index))}>
        <IconDelete className="pdf-editor__icon"/>
      </button>
    </div>,
  );

  useEffect(() => {
    const main = document.querySelector('.main');
    main.classList.add('main--no-padding');

    return () => {
      main.classList.remove('main--no-padding');
    };
  }, []);

  useEffect(() => {
    const editorRoot = instanceRef.current;

    htmlFileRequest.sendApiRequest({
      onSuccess(response) {
        editorRoot.innerHTML = response.data;
        editorRoot.addEventListener('click', edit, false);

        //  Вытаскивает скрипт из подключаемого скрипта
        // const re = /<script\b[^>]*>([\s\S]*?)<\/script>/g;
        // const match = response.data.match(re);
        // for (let i = 0; i < match.length; i++) {
        //   const res = match[i].replace(/<script>|<\/script>/gi, '');
        //   const script = document.createElement('script');
        //   script.innerHTML = res;
        //   document.head.appendChild(script);
        // }

        scope.current.scaleFactor = getScaleFactor(editorRoot);
        document.documentElement.style.setProperty('--matrix-border-width', `${2 / scope.current.scaleFactor}px`);
        document.documentElement.style.setProperty('--matrix-border-radius', `${3 / scope.current.scaleFactor}px`);
        document.documentElement.style.setProperty('--matrix-obj-controls-size', `${8 / scope.current.scaleFactor}px`);
        document.documentElement.style.setProperty('--matrix-edit-text-size', `${30 / scope.current.scaleFactor}px`);
        document.documentElement.style.setProperty('--matrix-edit-text-right', `${-35 / scope.current.scaleFactor}px`);
      },
      onError(error, time) {
        sendEvent({
          category: 'html-to-pdf',
          action: 'error',
          label: `${getErrorName(error)}:${time}`,
        });
      },
    });

    return () => {
      document.documentElement.style.removeProperty('--matrix-border-width');
      document.documentElement.style.removeProperty('--matrix-border-radius');
      document.documentElement.style.removeProperty('--matrix-obj-controls-size');
      document.documentElement.style.removeProperty('--matrix-edit-text-size');
      document.documentElement.style.removeProperty('--matrix-edit-text-right');

      if (editorRoot) {
        editorRoot.removeEventListener('click', edit, false);
        clearEdit();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleKeydown = (event) => {
      if (document.querySelector('.obj-controls')) {
        selectObject(event, null, keyEventCount);
      }
    };
    const handleKeyup = () => setKeyEventCount(0);

    document.addEventListener('keydown', handleKeydown, false);
    document.addEventListener('keyup', handleKeyup, false);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('keyup', handleKeyup);
    };
  }, [keyEventCount]);

  useSmallFooter();

  const clearEdit = () => {
    objectEditStore.setObjectEdit(false);
    store.setIsEditing(false);
    const controls = document.querySelector('.obj-controls');
    if (controls) {
      const parent = controls.parentNode;
      if (parent.classList.contains('t')) {
        if (parent.innerText.includes('\n') || parent.innerText.includes('</br>')) {
          parent.style.height = 'max-content';
        }
        if (parent.classList.contains('obj-moved')) {
          parent.classList.remove('obj-moved');
          parent.style.top = parent.offsetTop - parent.offsetHeight + (controls.offsetHeight * scope.current.scaleFactor) + 'px';
        }
      }
      controls.remove();
    }
    document.querySelectorAll('.obj-edit').forEach((element) => {
      element.classList.remove('obj-edit');
    });
  };

  const edit = (event, element) => {
    element = element || event.target;
    editorPanelButtonStore.setIsOpenModal('')
    if (element.closest('.obj')) {
      element = element.closest('.obj');
    }

    if (element.closest('.t')) {
      editorPanelButtonStore.setTab('edit')
    }

    if (document.querySelector('.obj-input-active')) {
      return;
    }

    clearEdit();
    if (element.classList.contains('t') || element.classList.contains('obj')) {
      store.setIsEditing(true);

      element.classList.add('obj-edit');
      const controls = document.createElement('div');
      controls.className = 'obj-controls';
      const image = element.querySelector('.photo-image')
      if (image) {
        controls.style.height = image.clientHeight + 'px';
        controls.style.width = image.clientWidth + 'px';
      }
      else {
        controls.style.height = element.clientHeight + 'px';
        controls.style.width = element.clientWidth + 'px';
      }

      if (element.classList.contains('obj-resizable')) {
        controls.innerHTML = '<p class="obj-resizer b-r"></p>';
      }

      if (element.classList.contains('t') || element.classList.contains('obj-text')) {
        controls.innerHTML += '<button class="obj-edit-text"><img alt="edit" src="/images/edit.svg"></button>';
        const computedStyles = window.getComputedStyle(element);
        let size = parseInt(computedStyles.fontSize, 10);

        element.style.fontSize = size + 'px';
        element.style.color = rgbToHex(computedStyles.color);
        element.style.fontWeight = computedStyles.fontWeight;
        element.style.fontFamily = computedStyles.fontFamily;
        element.style.textAlign = computedStyles.textAlign;
        // element.style.height = size + 'px';
        // controls.style.height = size + 'px';

        objectEditStore.setObjectEdit({element});
        objectEditStore.setFontFamily(computedStyles.fontFamily);
        objectEditStore.setTextAlign(computedStyles.textAlign);
        objectEditStore.setFontSize(size.toString());
        objectEditStore.setFontColor(rgbToHex(computedStyles.color));
      } else {
        objectEditStore.setObjectEdit({
          element,
        });
      }

      element.appendChild(controls);

      controls.addEventListener('mousedown', selectObject, false);

      const mobile= /Android|webOS|iPhone|iPad|iPod|BlackBerry/i.test(navigator.userAgent);
      if (mobile) {
        let lastClick = 0;
        let initialX = null;
        let initialY = null;

        controls.addEventListener('touchstart', function (e) {
          initialX = e.touches[0].clientX;
          initialY = e.touches[0].clientY;
          // disable browser default zoom on double tap
          e.preventDefault();
          let date = new Date();
          let time = date.getTime();
          const time_between_taps = 400; // ms

          if (time - lastClick < time_between_taps) {
            const event = new MouseEvent('mousedown', {
              'view': window,
              'bubbles': true,
              'cancelable': true,
              "detail": 2,
            });
            controls.dispatchEvent(event);
          }
          if (e.target.classList.contains('obj-edit-text')) {
            const btnEditClick = new MouseEvent('mousedown', {
              'view': window,
              'bubbles': true,
              'cancelable': true,
              "detail": 2,
            });
            controls.dispatchEvent(btnEditClick)
          }
          lastClick = time;
        }, false)


        controls.addEventListener('touchmove', function (e) {
          if (initialX === null || initialY === null) {
            return;
          }

          const deltaX = e.touches[0].clientX - initialX;
          const deltaY = e.touches[0].clientY - initialY;
          const sensitivity = 2;

          if (Math.abs(deltaX) > sensitivity) {
            if (deltaX > 0) {
              // Move right
              const event = new KeyboardEvent('keydown', {
                'view': window,
                'bubbles': true,
                'cancelable': true,
                'key': 'ArrowRight',
                'keyCode': 39,
              });
              controls.dispatchEvent(event);
            } else {
              // Move left
                const event = new KeyboardEvent('keydown', {
                    'view': window,
                    'bubbles': true,
                    'cancelable': true,
                    'key': 'ArrowLeft',
                    'keyCode': 37,
                });
                controls.dispatchEvent(event);
            }
          }

            if (Math.abs(deltaY) > sensitivity) {
                if (deltaY > 0) {
                    // Move down
                    const event = new KeyboardEvent('keydown', {
                        'view': window,
                        'bubbles': true,
                        'cancelable': true,
                        'key': 'ArrowDown',
                        'keyCode': 40,
                    });
                    controls.dispatchEvent(event);
                } else {
                    // Move up
                    const event = new KeyboardEvent('keydown', {
                        'view': window,
                        'bubbles': true,
                        'cancelable': true,
                        'key': 'ArrowUp',
                        'keyCode': 38,
                    });
                    controls.dispatchEvent(event);
                }
            }
          initialX = e.touches[0].clientX;
          initialY = e.touches[0].clientY;
        }, false)

        controls.addEventListener('touchend', () => {
          initialX = null;
          initialY = null;
        }, false);

      }
    }
  };

  const selectObject = (event, objectElement) => {
    if (event.detail === 2) {
      objectElement = objectElement || event.target.parentNode;

      return initTextEditor(event, (input) => {
          const element = input.closest('.t') || input.closest('.obj');
          if (element.classList.contains('obj-moved')){
            element.classList.remove('obj-moved');
          }

          element.classList.remove('obj-input-active');
          element.innerHTML = input.value;
          edit(null, element);
        });
      }

    store.setIsEditing(true);
    if (!objectElement) {
      const rect = event.target.getBoundingClientRect();
      scope.current.offset.y = event.clientY - rect.top;
      scope.current.offset.x = event.clientX - rect.left;
    }

    objectElement = objectElement || event.target.parentNode;
    if (objectElement.classList.contains('obj-controls')) {
      if (event.target.classList.contains('obj-resizer')) {
        return initResize(event, objectElement, instanceRef.current, () => {
          edit(null, objectElement);
        });
      }

      if (event.target.classList.contains('obj-edit-text')) {
        return initTextEditor(event, (input) => {
          const element = input.closest('.t') || input.closest('.obj');
          element.classList.remove('obj-input-active');
          element.innerHTML = input.value;
          edit(null, element);
        });
      }
    }

    switch (event.type) {
      case 'mousedown':
      case 'click':
        initDrag(event, objectElement, instanceRef.current, scope.current.offset, () => {
          edit(null, objectElement);
        });
        break;
      case 'keydown':
        setKeyEventCount((prev) => prev + 1);
        initMoveByKeys(event, scope.current.scaleFactor, keyEventCount);
        break;
    }
  };

  const handleEditErrorModalHide = useCallback(() => {
    store.setIsError(false);
  }, []);

  useEffect(() => {
    if (editError) {
      store.setIsError(true);
      if (editError.message.includes('524')) {
        sendEvent({
          category: 'pdf-editor',
          action: 'error',
          label: `${editError.message}`,
        });
      }
    }
  }, [editError, store]);

  const setPrintPageParams = () => {
    const pages = instanceRef.current.querySelectorAll('#page-container img');
    const scale = 0.8; // puppeteer parameter
    const page = pages[0];
    const { format } = getFileFormat(page.width, page.height, zoom);
    const { landscape } = getFileOrientation(page.width, page.height);

    return { format, landscape, scale };
  };

  const getScaleFactor = (rootElement) => {
    const pageInfoElement = rootElement.querySelector('.pi');
    const { ctm } = JSON.parse(pageInfoElement.dataset.data);
    const result = ctm[0] / 4;

    if (!result) {
      throw new Error('Scale factor not defined');
    }

    return result;
  };

  return (
    processing
      ? <LargeLoader />
      : <div className="pdf-editor" tabIndex="0" onKeyUp={(event) => {
        if (event.key === 'Delete' && objectEditStore.objectEdit) {
          if (!objectEditStore.objectEdit.element.className.includes('obj-input-active')){
            objectEditStore.setObjectEdit(objectEditStore.objectEdit.element.remove());
            store.setIsEditing(false);
          }
        }
      }}>
        <SignatureModal />
        <ImageModal />
        <div className="pdf-editor__header_toolbar">
          <div className="pdf-editor__header">
            <EditorPanelButton text="text" icon={IconText} isTab={editorPanelButtonStore.tab === 'add'} id="textButton"
                               tooltip="text" tooltipVariant="bottom" onClick={handleAddTextClick}/>
           {/*<EditorPanelButton text="shapes" isTab={tab === 'shapes'} icon={IconShapes} id="shapesButton" */}
           {/*                   tooltip="shapes" tooltipVariant="left" onClick={handleAddTextClick}/>*/}
            <ImageSelect images={imageButtons}/>
            <FormSelect selectForms={selectForms}/>
            <SignSelect signatures={signatureButtons}/>
            <HighLightSelect/>
          </div>
          <div className="pdf-editor__header-secondary">
            <EditorPanel/>
          </div>
        </div>
        <div ref={instanceRef} className={classNames(
          'pdf-editor__root',
          store.isEditing && 'edit-active',
        )}>
        </div>
        <div className="pdf-editor__footer">
          <Container className="pdf-editor__container">
            <Button
              className="pdf-editor__save-button"
              size="md"
              variant="primary"
              disabled={disabled || !store.file}
              onClick={handleSaveClick}
            >
              {t('convert-and-edit:editing.save-pdf')}
            </Button>
          </Container>
        </div>
        <ErrorModal
          title={t('convert-and-edit:processing-error-modal.title')}
          text={t('convert-and-edit:processing-error-modal.text')}
          show={store.isError}
          onHide={handleEditErrorModalHide}
        />
      </div>
  );
};

PdfEditor.displayName = 'PdfEditor';

PdfEditor.propTypes = {
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
  zoom: PropTypes.number.isRequired,
  tab: PropTypes.string,
};

export default PdfEditor;
